import { LessonProgressIDBData } from '@customtypes/lessonProgress';
import {
  LESSON_PROGRESS_DB_NAME,
  LESSON_PROGRESS_DB_VERSION,
  LESSON_PROGRESS_DB_STORE_NAME,
} from '@utils/constants';
import { DBSchema, openDB } from 'idb';

interface ProgressDB extends DBSchema {
  [LESSON_PROGRESS_DB_STORE_NAME]: {
    key: number;
    value: LessonProgressIDBData[];
  };
}

/**
 * Open IndexedDB using `LESSON_PROGRESS_DB_NAME` with
 * `LESSON_PROGRESS_DB_VERSION` and return the database promise.
 *
 * @returns db instance promise
 */
async function getDB() {
  const db = await openDB<ProgressDB>(
    LESSON_PROGRESS_DB_NAME,
    LESSON_PROGRESS_DB_VERSION,
    {
      upgrade(db) {
        db.createObjectStore(LESSON_PROGRESS_DB_STORE_NAME);
      },
    },
  );
  return db;
}

/**
 * Get lesson progress data for a specific tutorial from IndexedDB.
 *
 * @returns lesson progress data for a specific tutorial from IndexedDB
 */
export async function getSavedLessonProgressFromIDB(
  tutorialId: number,
): Promise<LessonProgressIDBData[]> {
  const db = await getDB();
  return (await db.get(LESSON_PROGRESS_DB_STORE_NAME, tutorialId)) || [];
}

/**
 * Save a single lesson progress object to IndexedDB based on the tutorial id
 * it belongs to.
 *
 * @param lessonProgress
 * @returns Promise<IDBValidKey>
 */
export async function saveLessonProgressInIDB(
  tutorialId: number,
  lessonProgress: LessonProgressIDBData,
) {
  const db = await getDB();
  const existingLessonProgress =
    await getSavedLessonProgressFromIDB(tutorialId);
  const newLessonProgress = [...existingLessonProgress, lessonProgress];
  return db.put(LESSON_PROGRESS_DB_STORE_NAME, newLessonProgress, tutorialId);
}

/**
 * Clear all lesson progress data from IndexedDB.
 */
export async function clearAllLessonProgressFromIDB() {
  return (await getDB()).clear(LESSON_PROGRESS_DB_STORE_NAME);
}
